import React, { useEffect } from 'react';
import styled from 'styled-components';
import Button from '@ingka/button';
import { useTranslation } from '@ikea-customer-self-serve/gatsby-plugin-translations';
import { showError } from '../reducers/error/errorReducer';
import { colors } from '../styles';
import { useStateDispatch, useStateSelector } from '../store';
import {
  updateAddressInSearch,
  updateCityInSearch,
  updatePostCodeInSearch,
} from '../reducers/search/searchReducer';

interface DaumData {
  address: string;
  zonecode: string;
}

type OnComplete = (resp: DaumData) => void;

interface Daum {
  Postcode: {
    new (construct: { oncomplete: OnComplete }): {
      open: () => void;
    };
  };
}

declare global {
  interface Window {
    daum?: Daum;
  }
}

const WHAM_SCRIPT_SRC =
  'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';

const StyledButton = styled(Button)`
  margin: 15px 0;
`;

const TextBox = styled.span`
  border: 1px solid ${colors.neutral.grey500};
  padding: 15px;
  color: ${colors.text.default};
  min-width: 300px;
`;

const useScript = (url: string, async = false): void => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = url;
    script.async = async;
    document.body.appendChild(script);
    return (): void => {
      document.body.removeChild(script);
    };
  }, [url, async]);
};

const DaumZipComponent: React.FC = (): JSX.Element => {
  useScript(WHAM_SCRIPT_SRC);
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const searchPostCode = useStateSelector((state) => state.search.postCode);
  const searchAddress = useStateSelector((state) => state.search.address);
  const userPostCode = useStateSelector((state) => state.user.postCode);
  const userAddress = useStateSelector((state) => state.user.streetAddress);
  const postCode = searchPostCode || userPostCode;
  const streetAddress = searchAddress || userAddress;

  const activateError = (): void => {
    dispatch(
      showError({
        title: t('components.error.zipCodeFail.title'),
        description: t('components.error.zipCodeFail.description'),
        button: t('components.error.zipCodeFail.close'),
      }),
    );
  };
  const onComplete = (resp: DaumData): void => {
    dispatch(updatePostCodeInSearch(resp.zonecode));
    dispatch(updateAddressInSearch(resp.address));
    dispatch(updateCityInSearch('대한민국'));
  };

  const handleOnClick = (): void => {
    if (typeof window === 'undefined' || !window.daum) {
      activateError();
    } else {
      const { daum } = window;
      new daum.Postcode({
        oncomplete: onComplete,
      }).open();
    }
  };

  const buttonText = postCode
    ? t('components.zipCode.editZip')
    : t('components.zipCode.findZip');

  return (
    <>
      <StyledButton
        onClick={handleOnClick}
        type="emphasised"
        text={buttonText}
      />
      {postCode && (
        <p>
          <TextBox>
            {[postCode, streetAddress].filter(Boolean).join(', ')}
          </TextBox>
        </p>
      )}
    </>
  );
};

export default DaumZipComponent;
